import React from 'react'
import { useDetails } from './DetailsContext'
import DetailsTable from './components/DetailsTable'
import DetailEditSheet from './components/DetailEditSheet'
import notify from 'components/ui/notify'
import { deleteSiteDetail } from 'slices/siteDetailsSlice'
import { useDispatch } from 'react-redux'

interface Details {
	_id: string
	detailLabel: string
	description: string
	detail: string
	detailTextValue: string
	detailNumericValue: string
	detailCurrencyValue: string
	detailDateValue: string
	detailType: string
	projectid: string
	createdAt?: string
	packageType?: string
	packageid?: string
	updatedAt?: string
	userid?: string
}

export interface Props {
	className?: string
	details: Details[]
	deleteDetail?: boolean
	handleSubmit: Function
	onAdd: Function
}

const Details = ({ details = [], handleSubmit, onAdd }: Props) => {
	const dispatch = useDispatch()

	const {
		state: { openPopup, recordForEdit },
		actions: { setOpenPopup, setRecordForEdit },
	} = useDetails()

	const hasDetails = details.length > 0

	const getFormattedDetails = () =>
		details.map(v => ({
			id: v._id,
			key: v.detailLabel,
			description: v.description,
			value: v.detailType === 'Currency' ? `${v.detail}` : v.detail,
			textValue: v.detailTextValue,
			numericValue: v.detailNumericValue,
			currencyValue: v.detailCurrencyValue,
			dateValue: v.detailDateValue,
			detailType: v.detailType,
			projectid: v.projectid,
		}))

	const getDetailById = (id: string) => details.find((detail: any) => detail._id === id)

	const handleEditDetail = (id: string) => {
		setRecordForEdit(getDetailById(id))
		setOpenPopup(true)
	}

	const handleDeleteDetail = async (detail: any) => {
		await notify({
			title: `Delete ${detail.key}?`,
			text: `This will delete the project detail '${detail.key}' from this project.`,
			icon: 'warning',
			buttons: {
				cancel: {
					text: 'Cancel',
					value: null,
					visible: true,
					className: '',
					closeModal: true,
				},
				confirm: {
					text: 'Delete',
					value: true,
					visible: true,
					className: '',
					closeModal: true,
				},
			},
		}).then((willDelete: boolean) => {
			if (willDelete) {
				dispatch(deleteSiteDetail(detail.id))
				notify({
					title: `Success!`,
					text: ` ${detail.key} has been deleted.`,
					icon: 'success',
				})
			}
		})
	}

	const filteredDetails = {
		currency: {
			title: 'Currency',
			details: getFormattedDetails().filter(detail => detail.detailType === 'Currency'),
		},
		date: {
			title: 'Dates',
			details: getFormattedDetails().filter(detail => detail.detailType === 'Date'),
		},
		number: {
			title: 'Numeric',
			details: getFormattedDetails().filter(detail => detail.detailType === 'Number'),
		},
		text: {
			title: 'Text',
			details: getFormattedDetails().filter(detail => detail.detailType === 'Text'),
		},
	}

	const handleSheetSave = (values: any, resetForm: () => void) => {
		const isAdd = !values._id || values._id === 0
		if (isAdd) {
			onAdd(values, resetForm)
		} else {
			handleSubmit(values, resetForm)
		}
	}

	const handleSheetClose = () => {
		setOpenPopup(false)
		setRecordForEdit(null)
	}

	return (
		<div className="max-w-[900px] mx-auto">
			{hasDetails &&
				Object.keys(filteredDetails).map((detailType, index) => {
					if (filteredDetails[detailType].details.length > 0) {
						return (
							<DetailsTable
								key={index}
								title={filteredDetails[detailType].title}
								details={filteredDetails[detailType].details}
								handleDeleteDetail={handleDeleteDetail}
								handleEditDetail={handleEditDetail}
							/>
						)
					}
					return null
				})}

			{!hasDetails && (
				<div className="flex flex-col items-center justify-center py-12 text-center">
					<h2 className="text-lg font-semibold text-gray-600">We don't have any details, yet.</h2>
				</div>
			)}

			<DetailEditSheet
				open={openPopup}
				onClose={handleSheetClose}
				record={recordForEdit}
				onSave={handleSheetSave}
			/>
		</div>
	)
}

export default Details
